import * as React from "react";
import * as ToastPrimitive from "@radix-ui/react-toast";
import { AlertCircle, CheckCircle2, X } from "lucide-react";
import { cn } from "@/lib/utils/cn";

type ToastVariant = "success" | "error" | "default";

interface ToastItem {
  id: number;
  message: string;
  variant: ToastVariant;
}

interface ToastContextValue {
  toast: (message: string, variant?: ToastVariant) => void;
}

const ToastContext = React.createContext<ToastContextValue | null>(null);

export function useToast(): ToastContextValue {
  const ctx = React.useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within <ToastProvider>");
  return ctx;
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = React.useState<ToastItem[]>([]);

  const toast = React.useCallback((message: string, variant: ToastVariant = "default") => {
    setItems((prev) => [...prev, { id: Date.now() + Math.random(), message, variant }]);
  }, []);

  const remove = (id: number) => setItems((prev) => prev.filter((t) => t.id !== id));

  return (
    <ToastContext.Provider value={{ toast }}>
      <ToastPrimitive.Provider swipeDirection="right" duration={4000}>
        {children}
        {items.map((t) => (
          <ToastPrimitive.Root
            key={t.id}
            onOpenChange={(open) => {
              if (!open) remove(t.id);
            }}
            className={cn(
              "flex items-start gap-3 rounded-lg border border-border bg-surface-2 p-4 shadow-lg",
              "data-[state=open]:animate-[toast-in_200ms_ease-out] data-[state=closed]:animate-[toast-out_150ms_ease-in]",
            )}
          >
            <span className="mt-0.5 shrink-0">
              {t.variant === "success" ? (
                <CheckCircle2 className="h-5 w-5 text-accent" />
              ) : t.variant === "error" ? (
                <AlertCircle className="h-5 w-5 text-danger" />
              ) : null}
            </span>
            <ToastPrimitive.Description className="text-sm text-text">
              {t.message}
            </ToastPrimitive.Description>
            <ToastPrimitive.Close
              className="ml-auto text-text-muted transition-colors hover:text-text"
              aria-label="Tutup"
            >
              <X className="h-4 w-4" />
            </ToastPrimitive.Close>
          </ToastPrimitive.Root>
        ))}
        <ToastPrimitive.Viewport className="fixed bottom-4 right-4 z-[1000] flex w-[min(92vw,22rem)] flex-col gap-2 outline-none" />
      </ToastPrimitive.Provider>
    </ToastContext.Provider>
  );
}
